/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useState } from 'react';

// Mantine
import { Box, Title } from '@mantine/core';

// Components
import { Select } from '@components/atoms/Select';
import { Button } from '@components/atoms/Button';
import { Text } from '@components/atoms/Text';

// Styles
import classes from './UpdateSiteForm.module.css';

// Utils
import {
	convertArrayToLabelValue,
	convertMWHToKWHValue,
	convertTToKgValue,
	getUnitFromProjectUnit,
} from '@utils/functions';

// Icons
import useStore from '@store/index';

// I18n
import { useTranslation } from 'react-i18next';
import { useOrganizations } from 'shared/api/get-organizations';
import { useMeasurabilities } from 'shared/api/get-measurabilities';
import { useMediaQuery } from '@mantine/hooks';
import {
	Divider,
	NumericInput,
	TextInputV2,
} from '@components/atoms';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { useProducers } from 'shared/api/get-producers';

import { useParams } from 'react-router';
import { useMeasurabilityProject } from '@features/catalogs/meausrability-projects/details/api/get-measurability-project-by-id';
import { TMeasurabilityProject } from '@global-types/api/api';
import { useUpdateSite } from '@features/resources/sites/api/update-site';
import { UseFormReturnType } from '@mantine/form';
import { TMesurabilityProject } from '@features/resources/measurabilities/types';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const UpdateSiteForm = (props: {
	formSite: UseFormReturnType<any>;
	onCancel: () => void;
	onBack: () => void;
	isPreviousMeasurabilityProjectDetails: boolean;
	selectedItem: any;
}) => {
	const { selectedItem } = props;
	const [organizationId, setOrganizationId] =
		useState('');
	const [producerId, setProducerId] = useState('');

	const { sitesPage, sitesPageSize } = useStore();

	const { t } = useTranslation();

	const { projectId } = useParams();

	const { data: projectFromId, isFetched } =
		useMeasurabilityProject({
			projectId: projectId ? projectId : '',
			queryConfig: {
				enabled: !!projectId,
			},
		});

	const { data: projects } = useMeasurabilities({
		entity: producerId ? producerId : [],
	});
	const { data: organizations } = useOrganizations();

	const { data: producers } = useProducers({
		organization: organizationId ? organizationId : [],
	});

	const updateSiteMutation = useUpdateSite({
		page: sitesPage,
		size: sitesPageSize,
		mutationConfig: {
			onSuccess() {
				props.onBack();
			},
		},
	});

	const isBigScreen = useMediaQuery('(min-width: 120em)');

	const getForm = () => {
		/* ******************************** HELPERS *********************************** */

		const handleSubmitForm = () => {
			props.formSite.validate();
			if (!props.formSite.isValid()) return;
			if (!props.formSite.isDirty()) {
				props.onBack();
			} else {
				const unitProject =
					projects.find(
						(_project: TMesurabilityProject) =>
							_project.id ===
							props.formSite.values.project
					) &&
					projects.find(
						(_project: TMesurabilityProject) =>
							_project.id ===
							props.formSite.values.project
					).unit
						? getUnitFromProjectUnit(
								projects.find(
									(_project: TMesurabilityProject) =>
										_project.id ===
										props.formSite.values.project
								).unit
							)
						: '';

				const {
					name,
					maxValue,
					minValue,
					project,
					place,
					countryCode,
				} = props.formSite.getValues();

				const _minValue =
					unitProject?.toLowerCase() === 'MWH'
						? convertMWHToKWHValue(minValue)
						: convertTToKgValue(minValue);

				const _maxValue =
					unitProject?.toLowerCase() === 'MWH'
						? convertMWHToKWHValue(maxValue)
						: convertTToKgValue(maxValue);

				const body = {
					...(props.formSite.isDirty('name')
						? {
								name,
							}
						: {}),
					production: {
						...{ maxValue: _maxValue },
						...{ minValue: _minValue },
					},

					...(props.formSite.isDirty('project')
						? {
								project,
							}
						: {}),
					...(props.formSite.isDirty('place')
						? {
								location: {
									place,
									countryCode,
								},
							}
						: {}),

					...(props.formSite.isDirty('countryCode')
						? {
								location: {
									countryCode,
								},
							}
						: {}),
				};

				updateSiteMutation.mutate({
					id: selectedItem.id,
					body,
				});
			}
		};

		/* ******************************** CONSTANTS ******************************* */

		const requiredFields = [
			'organization',
			'entity',
			'site',
			'project',
		];
		const isValidSelects = requiredFields.every((field) =>
			props.formSite.isValid(field)
		);
		const isEnableToAdd = isValidSelects;

		const {
			onChange: onChangeProject,
			...organizationProps
		} = props.formSite.getInputProps('organization');

		const { onChange: onChangeSite, ...entityProps } =
			props.formSite.getInputProps('entity');

		const handleChangeEntity = (value: string) => {
			props.formSite.setFieldValue('entity', value);
			props.formSite.setFieldValue('project', null);

			setProducerId(value);
		};

		const {
			onChange: onChangeProject2,
			...projectProps
		} = props.formSite.getInputProps('project');

		const handleChangeProject = (value: string) => {
			props.formSite.setFieldValue('project', value);
		};

		const handleChangeOrganization = (value: string) => {
			props.formSite.setFieldValue('organization', value);
			setOrganizationId(value);
			props.formSite.setFieldValue('entity', null);
			props.formSite.setFieldValue('project', null);
		};

		/* ******************************** RENDERING ******************************* */
		return (
			<form
				className={classes.containerForm}
				onSubmit={props.formSite.onSubmit(
					(values: any) => {}
				)}
			>
				<Box className={classes.containerTitle}>
					<Title className={classes.titleForm}>
						{isFetched && projectFromId
							? projectFromId.name
							: t('global.sites')}
					</Title>
					<Text className={classes.subTitleForm}>
						/ {t('sites.updateSite')}
					</Text>
				</Box>

				{/* Row for Producer */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							disabled
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.organization')}
							withAsterisk
							data={convertArrayToLabelValue(
								organizations
							)}
							placeholder={t('global.selectOrganization')}
							onChange={handleChangeOrganization}
							{...organizationProps}
						/>
					</Box>
				</Box>
				{/* Row for Project & Site */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							disabled
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.producer')}
							withAsterisk
							data={convertArrayToLabelValue(producers)}
							placeholder={t('global.selectEntity')}
							onChange={handleChangeEntity}
							{...entityProps}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<Select
							disabled
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.project')}
							withAsterisk
							data={convertArrayToLabelValue(projects)}
							placeholder={t('global.selectProject')}
							onChange={handleChangeProject}
							{...projectProps}
						/>
					</Box>
				</Box>
				<Divider size={'xs'} />
				{/* Row for Site name */}
				<Box className={classes.containerInput}>
					<TextInputV2
						withAsterisk
						size={isBigScreen ? 'md' : 'sm'}
						label={t('global.name')}
						placeholder={t('global.name')}
						{...props.formSite.getInputProps('name')}
					/>
				</Box>
				{/* Row for Country & Place */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.country')}
							data={EUROPEAN_COUNTRIES}
							placeholder={t('global.country')}
							{...props.formSite.getInputProps(
								'countryCode'
							)}
						/>
					</Box>
					<Box className={classes.containerInput}>
						<TextInputV2
							disabled={
								!props.formSite.getValues().countryCode
							}
							withAsterisk
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.place')}
							placeholder={t('global.place')}
							{...props.formSite.getInputProps('place')}
						/>
					</Box>
				</Box>
				{/* Row for MinValue & MaxValue */}
				<Box className={classes.row}>
					<Box className={classes.containerInput}>
						<NumericInput
							max={99999}
							decimalScale={
								Math.trunc(
									props.formSite.getInputProps('minValue')
										.value
								) == 0
									? 1
									: 2
							}
							unit={
								projects.find(
									(_project: TMeasurabilityProject) =>
										_project.id ===
										props.formSite.values.project
								) &&
								projects.find(
									(_project: TMeasurabilityProject) =>
										_project.id ===
										props.formSite.values.project
								).unit
									? getUnitFromProjectUnit(
											projects.find(
												(
													_project: TMeasurabilityProject
												) =>
													_project.id ===
													props.formSite.values.project
											).unit
										)
									: ''
							}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('sites.form.labelMinProduction')}
							defaultValue={0}
							{...props.formSite.getInputProps(
								'minValue'
							)}
						/>
					</Box>

					<Box className={classes.containerInput}>
						<NumericInput
							max={99999}
							decimalScale={
								Math.trunc(
									props.formSite.getInputProps('maxValue')
										.value
								) == 0
									? 1
									: 2
							}
							size={isBigScreen ? 'md' : 'sm'}
							label={t('sites.form.labelMaxProduction')}
							defaultValue={0}
							unit={
								projects.find(
									(_project: TMeasurabilityProject) =>
										_project.id ===
										props.formSite.values.project
								) &&
								projects.find(
									(_project: TMeasurabilityProject) =>
										_project.id ===
										props.formSite.values.project
								).unit
									? getUnitFromProjectUnit(
											projects.find(
												(
													_project: TMeasurabilityProject
												) =>
													_project.id ===
													props.formSite.values.project
											).unit
										)
									: ''
							}
							{...props.formSite.getInputProps(
								'maxValue'
							)}
						/>
					</Box>
				</Box>
				<Divider size={'xs'} />
				{/* Row for Buttons */}
				<Box className={classes.rowButtons}>
					<Box className={classes.containerButtonCancel}>
						<Button
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							variant="transparent"
							onClick={() => {
								props.onCancel();
							}}
						>
							{t('global.cancel')}
						</Button>
					</Box>
					<Box className={classes.containerButton}>
						<Button
							data-autofocus
							disabled={!props.formSite.isDirty()}
							fullWidth
							size={isBigScreen ? 'lg' : 'md'}
							onClick={handleSubmitForm}
							loading={updateSiteMutation.isPending}
						>
							{t('sites.updateSite')}
						</Button>
					</Box>
				</Box>
			</form>
		);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.containerEmpty} />
			{getForm()}
			<Box className={classes.containerEmpty} />
		</Box>
	);
};
